import { ProjectServiceType, ProjectStatus, ProjectServiceResponse, ProjectResponse } from '@business/entities/project';
import { R } from '@core/utils/r';
import { InvoiceDiscountType } from '@business/entities/TransactionInvoice';
import { InvoiceFromData } from '@modules/projects/CreateProjectPage/ProjectForm';
import { Formatter } from '@core/utils/formatter';

export function getStatusTitle(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.pending:
      return 'Pending Setup';
    case ProjectStatus.active:
      return 'Active';
    case ProjectStatus.completed:
      return 'Completed';
    case ProjectStatus.cancelled:
      return 'Canceled';
    case ProjectStatus.all:
      return 'All';
    case ProjectStatus.overdue:
      return 'Overdue';
  }
  return '';
}

export function getStatusClass(status: ProjectStatus) {
  switch (status) {
    case ProjectStatus.pending:
      return 'badge-soft-info';
    case ProjectStatus.active:
      return 'badge-soft-primary';
    case ProjectStatus.completed:
      return 'badge-soft-success';
    case ProjectStatus.cancelled:
      return 'badge-soft-danger';
  }
}

export function getServiceTypes() {
  return [ProjectServiceType.project, ProjectServiceType.marketing, ProjectServiceType.recurring];
}

export function getServiceTypeTitle(type: ProjectServiceType, combined = false) {
  switch (type) {
    case ProjectServiceType.project:
      return 'Project';
    case ProjectServiceType.marketing:
      return combined ? 'Subscriptions' : 'Subscription on Subscription Board';
    case ProjectServiceType.recurring:
      return combined ? 'Subscriptions' : 'Subscription';
    case ProjectServiceType.inactive:
      return 'Inactive';
  }
}

export function getRStatuses() {
  return [ProjectStatus.active, ProjectStatus.completed, ProjectStatus.overdue, ProjectStatus.cancelled, ProjectStatus.all];
}

export function filterByCurrency(services: ProjectServiceResponse[], currency?: string) {
  if (!currency) return services;
  return services.filter(service => (service.currency || '').toLowerCase() === currency.toLowerCase() || R.isEmpty(service.currency));
}

export function getInvoiceTotal(invoiceFormValue: InvoiceFromData) {
  const subTotal = invoiceFormValue.lineItems.reduce((total, line) => total + line.qty * line.rate, 0);

  const discount = () => {
    if (!invoiceFormValue.discount.isDiscount) return 0;
    if (invoiceFormValue.discount.type === InvoiceDiscountType.fixed) {
      return invoiceFormValue.discount.value;
    } else {
      return (invoiceFormValue.discount.value * subTotal) / 100;
    }
  };

  return subTotal - discount();
}

export function getTimeTrackerColor(project: ProjectResponse) {
  const hoursTracked = (Formatter.formatSeconds(project.totalTimeTracked, 'h') as number) ?? 0;
  const minutesTracked = (Formatter.formatSeconds(project.totalTimeTracked, 'm') as number) ?? 0;
  const totalTime = project.hours;

  if (!totalTime) {
    return 'badge-soft-grey';
  }

  if (hoursTracked < totalTime) {
    return 'badge-soft-success';
  }

  if (hoursTracked > totalTime) {
    return 'badge-soft-danger';
  }

  if (hoursTracked === totalTime && minutesTracked > 0) {
    return 'badge-soft-danger';
  }

  return 'badge-soft-grey';
}

export function getTimeTrackedTooltip(project: ProjectResponse) {
  const totalTime = project.hours;

  const t = Formatter.formatSeconds(project.totalTimeTracked);
  if (!totalTime) {
    return `This project has ${t} of time tracked. No number of hours have seen set for this project.`;
  }

  return `This project has ${t} of time tracked out of ${totalTime} hours.`;
}
